import React from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import QuillPenLineIcon from 'remixicon-react/QuillPenLineIcon';
import ShieldCheckLineIcon from 'remixicon-react/ShieldCheckLineIcon';
import BankLineIcon from 'remixicon-react/BankLineIcon';
import Store2LineIcon from 'remixicon-react/Store2LineIcon';
import Key2LineIcon from 'remixicon-react/Key2LineIcon';
import CheckLineIcon from 'remixicon-react/CheckLineIcon';
import TimeLineIcon from 'remixicon-react/TimeLineIcon';
import CloseCircleLineIcon from 'remixicon-react/CloseCircleLineIcon';
import ExternalLinkLineIcon from 'remixicon-react/ExternalLinkLineIcon';
import { SetupStepId, SetupStepStatus } from '../../../services/helpers/guidedSetup';

export interface SetupStepAction {
    label: string;
    /** Why the action is disabled, shown as its tooltip. No reason means the action is enabled. */
    disabledReason?: string;
    /** Whether the action opens a Bits workflow in a new tab. */
    opensInNewTab?: boolean;
    onClick: () => void;
}

interface SetupStepCardProps {
    id: SetupStepId;
    number: number;
    status: SetupStepStatus;
    title: string;
    description: string;
    /** The badge text once the step is done. */
    doneLabel?: string;
    /** The badge text when the step failed. */
    failedLabel?: string;
    /** A line about where the step stands, shown instead of an action (the signed agreement). */
    statusLine?: string;
    action?: SetupStepAction;
    /** Whether the action is in progress (e.g. a Bits session being opened). */
    isBusy?: boolean;
}

type IconType = React.ComponentType<{ size?: number | string }>;

const icons: Record<SetupStepId, IconType> = {
    [SetupStepId.Agreement]: QuillPenLineIcon,
    [SetupStepId.Kyc]: ShieldCheckLineIcon,
    [SetupStepId.BankAccount]: BankLineIcon,
    [SetupStepId.Store]: Store2LineIcon,
    [SetupStepId.ApiKey]: Key2LineIcon,
};

const badgeIcons: Partial<Record<SetupStepStatus, IconType>> = {
    [SetupStepStatus.Done]: CheckLineIcon,
    [SetupStepStatus.Waiting]: TimeLineIcon,
    [SetupStepStatus.Failed]: CloseCircleLineIcon,
};

/** One step of the guided setup: icon, status badge, title, one line of help and one action. */
const SetupStepCard: React.FC<SetupStepCardProps> = ({
    id,
    number,
    status,
    title,
    description,
    doneLabel = 'Done',
    failedLabel = 'Failed',
    statusLine,
    action,
    isBusy,
}) => {
    const Icon = icons[id];
    const BadgeIcon = badgeIcons[status];
    const testId = `setup-step-${id}`;
    const badgeLabels: Record<SetupStepStatus, string> = {
        [SetupStepStatus.NotStarted]: `Step ${number}`,
        [SetupStepStatus.Waiting]: 'Waiting',
        [SetupStepStatus.Done]: doneLabel,
        [SetupStepStatus.Failed]: failedLabel,
    };

    return (
        <Card data-testid={testId} data-step-status={status}>
            <Top>
                <IconTile $step={id} aria-hidden="true">
                    <Icon size={22} />
                </IconTile>
                <Badge $status={status} data-testid={`${testId}-badge`}>
                    {BadgeIcon && <BadgeIcon size={14} />}
                    {badgeLabels[status]}
                </Badge>
            </Top>
            <Title>{title}</Title>
            <Description>{description}</Description>
            {statusLine && (
                <StatusLine data-testid={`${testId}-status`}>
                    <Dot $done={status === SetupStepStatus.Done} aria-hidden="true" />
                    {statusLine}
                </StatusLine>
            )}
            {action && (
                <ActionButton
                    type="button"
                    data-testid={`${testId}-action`}
                    disabled={!!action.disabledReason || isBusy}
                    title={action.disabledReason}
                    onClick={action.onClick}
                >
                    {action.label}
                    {action.opensInNewTab && (
                        <ExternalLinkLineIcon size={14} aria-label="Opens in a new tab" />
                    )}
                </ActionButton>
            )}
        </Card>
    );
};

const Card = styled.section`
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 1.4rem;
    padding: 2.4rem;
    background: ${props => props.theme.background};
    border: 0.1rem solid ${props => props.theme.colors.card.border};
    border-radius: 1rem;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 0.4rem;
        background: linear-gradient(90deg, #f2e21c 0%, #f39a12 30%, #e8481f 60%, #d4107e 100%);
    }
`;

const Top = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const stepTints = (theme: DefaultTheme): Record<SetupStepId, string> => ({
    [SetupStepId.Agreement]: theme.colors.accent4,
    [SetupStepId.Kyc]: theme.colors.success,
    [SetupStepId.BankAccount]: theme.colors.accent2,
    [SetupStepId.Store]: theme.colors.otherPrimary,
    [SetupStepId.ApiKey]: theme.colors.accent3,
});

const IconTile = styled.div<{ $step: SetupStepId }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 4.4rem;
    height: 4.4rem;
    border-radius: 1rem;
    color: ${props => props.theme.colors.text.primary};
    background: ${props => `${stepTints(props.theme)[props.$step]}33`};
`;

const badgeColor = (theme: DefaultTheme, status: SetupStepStatus) => {
    switch (status) {
        case SetupStepStatus.Done:
            return theme.colors.success;
        case SetupStepStatus.Waiting:
            return theme.colors.otherAccent3;
        case SetupStepStatus.Failed:
            return theme.colors.error;
        default:
            return theme.colors.text.secondary;
    }
};

const Badge = styled.span<{ $status: SetupStepStatus }>`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    font-family: ${props => props.theme.text.font.medium};
    color: ${props => badgeColor(props.theme, props.$status)};
`;

const Title = styled.h2`
    margin: 0;
    font-family: ${props => props.theme.text.font.bold};
    font-size: ${props => props.theme.text.size.xlarge};
    color: ${props => props.theme.colors.text.primary};
`;

const Description = styled.p`
    flex: 1;
    margin: 0;
    line-height: 1.5;
    color: ${props => props.theme.colors.text.secondary};
`;

const StatusLine = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 1.2rem 1.4rem;
    border: 0.1rem dashed ${props => props.theme.colors.border};
    border-radius: 0.6rem;
    font-family: ${props => props.theme.text.font.medium};
    color: ${props => props.theme.colors.text.primary};
`;

const Dot = styled.span<{ $done: boolean }>`
    flex: none;
    width: 0.8rem;
    height: 0.8rem;
    border-radius: 50%;
    background: ${props => (props.$done ? props.theme.colors.success : props.theme.colors.accent3)};
`;

const ActionButton = styled.button`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;
    min-height: 4.4rem;
    padding: 1.2rem 1.8rem;
    border: 0;
    border-radius: 0.6rem;
    font-family: ${props => props.theme.text.font.medium};
    font-size: ${props => props.theme.text.size.medium};
    cursor: pointer;
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.button.primary.text};

    &:focus-visible {
        outline: 0.2rem solid ${props => props.theme.colors.otherPrimary};
        outline-offset: 0.2rem;
    }

    ${props =>
        css`
            &:disabled {
                cursor: not-allowed;
                background: ${props.theme.colors.subtle.light};
                color: ${props.theme.colors.veryDark};
            }
        `}
`;

export default SetupStepCard;
